import React, { useState } from 'react';
import { Box, Typography, Paper, Stack, Button } from '@mui/material';
import { AttachFile, CloudUpload } from '@mui/icons-material';
import UploadDocumentDialog from './UploadDocumentDialog';
import DocumentsGrid from './DocumentsGrid';
import { DocumentUploadsControlProps } from './types';

/**
 * DocumentUploadsControl - reusable documents grid + upload/view dialogs, shared between
 * Tickets and Assets. Fully presentational: data and mutations come in as props.
 */
export const DocumentUploadsControl: React.FC<DocumentUploadsControlProps> = ({
  entityId,
  documents,
  isLoading,
  isError,
  error,
  currentUserName,
  defaultDocumentType = 'General',
  onUpload,
  onDownload,
  onDelete
}) => {
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  return (
    <Stack spacing={1.5}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 1.5
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <AttachFile sx={{ color: '#64748b', fontSize: 20 }} />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: '#1e293b'
              }}
            >
              Documents & Attachments
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setShowUploadDialog(true)}
            sx={{
              backgroundColor: '#162346',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                backgroundColor: '#1a2a52'
              }
            }}
          >
            Upload Documents
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: 1.5,
          overflow: 'hidden',
          p: 0
        }}
      >
        <DocumentsGrid
          entityId={entityId}
          documents={documents}
          isLoading={isLoading}
          isError={isError}
          error={error}
          currentUserName={currentUserName}
          onDownload={onDownload}
          onDelete={onDelete}
        />
      </Paper>

      <UploadDocumentDialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        onUpload={onUpload}
        defaultDocumentType={defaultDocumentType}
      />
    </Stack>
  );
};

export default DocumentUploadsControl;
